import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Search, UserPlus, RefreshCw, Activity, ArrowRight, UploadCloud } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import Portal from "@/components/Portal";
import PurgeDataButton from "@/components/PurgeDataButton";

export default function PatientSearch() {
  const { branch, user } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [scope, setScope] = useState("branch"); // branch | all
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const isAdmin = user?.role === "admin" || user?.role === "superadmin";
  const canBulk = isAdmin || user?.role === "manager";

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const params = { q: q || undefined };
      if (scope === "branch") params.branch_code = branch?.code;
      const { data } = await api.get("/patients", { params });
      setRows(data);
    } catch (err) {
      toast.error(formatApiError(err, "Search failed"));
    } finally {
      setLoading(false);
    }
  }, [q, scope, branch?.code]);

  useEffect(() => {
    const t = setTimeout(fetchRows, 200);
    return () => clearTimeout(t);
  }, [fetchRows]);

  const reregister = async (p) => {
    try {
      const { data } = await api.post(`/patients/${p.id}/reregister`, null, {
        params: { branch_code: branch.code, complaint: p.complaint || "" },
      });
      toast.success(`New token issued · ${data.op_token}`);
      fetchRows();
      setSelected(data);
    } catch (err) {
      toast.error(formatApiError(err, "Re-register failed"));
    }
  };

  const allocate = async (p) => {
    try {
      await api.post(`/patients/${p.id}/allocate-nursing`);
      toast.success("Sent to nursing queue");
      fetchRows();
    } catch (err) {
      toast.error(formatApiError(err, "Could not allocate"));
    }
  };

  return (
    <div className="max-w-6xl mx-auto animate-fade-up" data-testid="patient-search-page">
      <div className="text-xs uppercase tracking-[0.28em] text-[#5C5652] mb-3 ornament-dot">
        Patient records
      </div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl text-forest mb-1">
            Search patients
          </h1>
          <p className="text-[#5C5652]">Look up by name, phone, MRN or today&apos;s token.</p>
        </div>
        {isAdmin && (
          <Link
            to="/patients/new"
            data-testid="search-new-patient"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-forest text-white font-semibold hover:bg-forest-hover transition-colors"
          >
            <UserPlus className="h-4 w-4" strokeWidth={1.75} /> Register new
          </Link>
        )}
        {canBulk && (
          <Link
            to="/patients/bulk"
            data-testid="search-bulk-import"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg border border-sand bg-white hover:bg-[#F9F6F0] text-sm font-semibold text-forest"
          >
            <UploadCloud className="h-4 w-4" strokeWidth={1.75} /> Bulk import          </Link>
        )}
        <PurgeDataButton collection="patients" label="patients" onDone={fetchRows} />
      </div>

      <div className="bg-white border border-sand rounded-lg p-4 sm:p-5 mb-6">
        <div className="grid gap-4 sm:grid-cols-[1fr_auto]">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#5C5652]" strokeWidth={1.5} />
            <input
              data-testid="search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Name, phone, MRN or token…"
              className="w-full h-11 pl-10 pr-3 rounded-lg bg-white border border-sand focus:outline-none focus:ring-2 focus:ring-[#1A4331]/40 focus:border-[#1A4331] transition-colors"
            />
          </div>
          <div className="flex bg-[#F9F6F0] border border-sand rounded-lg p-1 h-11" data-testid="scope-toggle">
            {[
              { v: "branch", l: `This branch (${branch?.code})` },
              { v: "all", l: "All branches" },
            ].map((o) => (
              <button
                key={o.v}
                onClick={() => setScope(o.v)}
                data-testid={`scope-${o.v}`}
                className={`px-3 rounded-md text-sm font-medium transition-colors ${
                  scope === o.v ? "bg-forest text-white" : "text-[#5C5652] hover:text-forest"
                }`}
              >
                {o.l}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white border border-sand rounded-lg overflow-hidden">
        {loading ? (
          <div className="p-8 text-sm text-[#5C5652]">Searching…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center">
            <div className="font-display text-forest text-xl mb-1">No patients found</div>
            <p className="text-sm text-[#5C5652]">
              {q ? "Try a different search term." : "Register your first patient to get started."}
            </p>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="patients-table">
            <thead className="bg-[#F9F6F0] border-b border-sand text-[10px] uppercase tracking-wider text-[#5C5652]">
              <tr>
                <th className="text-left px-5 py-3 font-bold">Patient</th>
                <th className="text-left px-5 py-3 font-bold hidden sm:table-cell">Phone</th>
                <th className="text-left px-5 py-3 font-bold">MRN</th>
                <th className="text-left px-5 py-3 font-bold hidden md:table-cell">Branch</th>
                <th className="text-left px-5 py-3 font-bold">Token</th>
                <th className="text-left px-5 py-3 font-bold">Status</th>
                <th className="text-right px-5 py-3 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#E8E3D9]">
              {rows.map((p) => (
                <tr
                  key={p.id}
                  data-testid={`patient-row-${p.mrn}`}
                  className="hover:bg-[#F9F6F0] transition-colors cursor-pointer"
                  onClick={() => setSelected(p)}
                >
                  <td className="px-5 py-4">
                    <div className="font-semibold text-[#2A2624]">{p.name}</div>
                    <div className="text-xs text-[#5C5652]">
                      {p.age} · {p.gender} · visit #{p.visits}
                    </div>
                  </td>
                  <td className="px-5 py-4 hidden sm:table-cell text-[#5C5652]">{p.phone}</td>
                  <td className="px-5 py-4 font-mono text-xs text-forest">{p.mrn}</td>
                  <td className="px-5 py-4 hidden md:table-cell text-[#5C5652]">{p.branch_code}</td>
                  <td className="px-5 py-4 font-display text-forest">{p.op_token || "—"}</td>
                  <td className="px-5 py-4">
                    <StatusPill status={p.status} />
                  </td>
                  <td className="px-5 py-4 text-right">
                    {isAdmin && (
                      <div className="inline-flex gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          onClick={() => reregister(p)}
                          data-testid={`row-reregister-${p.mrn}`}
                          title="Re-register (new visit)"
                          className="h-8 w-8 rounded-md border border-sand text-[#5C5652] hover:text-forest hover:border-forest/40 hover:bg-[#F9F6F0] transition-colors inline-flex items-center justify-center"
                        >
                          <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.75} />
                        </button>
                        <button
                          onClick={() => allocate(p)}
                          data-testid={`row-allocate-${p.mrn}`}
                          title="Send to nursing"
                          className="h-8 w-8 rounded-md border border-sand text-[#5C5652] hover:text-forest hover:border-forest/40 hover:bg-[#F9F6F0] transition-colors inline-flex items-center justify-center"
                        >
                          <Activity className="h-3.5 w-3.5" strokeWidth={1.75} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Detail drawer */}
      {selected && (
        <Portal>
        <div
          className="fixed inset-0 bg-[#2A2624]/40 z-30 flex items-end sm:items-center justify-center p-0 sm:p-8"
          onClick={() => setSelected(null)}
          data-testid="patient-detail-overlay"
        >
          <div
            className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-lg border border-sand overflow-hidden animate-fade-up"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-sand flex items-start justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] text-[#5C5652] mb-1">
                  {selected.mrn}
                </div>
                <div className="font-display text-2xl text-forest">{selected.name}</div>
                <div className="text-sm text-[#5C5652]">
                  {selected.age} · {selected.gender} · {selected.phone}
                </div>
              </div>
              <StatusPill status={selected.status} />
            </div>
            <div className="px-6 py-5 space-y-3 text-sm">
              <Row label="Address" value={selected.address} />
              <Row label="Branch" value={selected.branch_code} />
              <Row label="Today's token" value={selected.op_token || "—"} />
              <Row label="Visit #" value={selected.visits} />
              {selected.complaint && <Row label="Complaint" value={selected.complaint} />}
              <Row
                label="Registered"
                value={new Date(selected.registered_at).toLocaleString()}
              />
            </div>
            {isAdmin && (
              <div className="px-6 py-4 bg-[#F9F6F0] border-t border-sand flex flex-wrap gap-2">
                <button
                  onClick={() => reregister(selected)}
                  data-testid="detail-reregister"
                  className="h-10 px-4 rounded-lg bg-forest text-white text-sm font-semibold hover:bg-forest-hover transition-colors inline-flex items-center gap-2"
                >
                  <RefreshCw className="h-4 w-4" strokeWidth={1.75} /> Re-register (new visit)
                </button>
                <button
                  onClick={() => allocate(selected)}
                  data-testid="detail-allocate"
                  className="h-10 px-4 rounded-lg border border-sand bg-white text-sm font-semibold hover:bg-[#F9F6F0] transition-colors inline-flex items-center gap-2"
                >
                  <Activity className="h-4 w-4" strokeWidth={1.75} /> Send to nursing
                </button>
                <button
                  onClick={() => setSelected(null)}
                  className="ml-auto h-10 px-4 rounded-lg text-[#5C5652] text-sm font-semibold hover:bg-white transition-colors"
                  data-testid="detail-close"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
        </Portal>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    registered: { bg: "bg-[#F0EBDE]", text: "text-[#5C5652]", label: "Registered" },
    in_nursing: { bg: "bg-[#E5A93D]/15", text: "text-[#B57F1D]", label: "In nursing" },
    in_consultation: { bg: "bg-[#1A4331]/10", text: "text-forest", label: "In consult" },
    done: { bg: "bg-[#1A4331]", text: "text-white", label: "Done" },
  };
  const s = map[status] || map.registered;
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded ${s.bg} ${s.text}`}>
      {s.label}
    </span>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline gap-4">
      <div className="w-32 text-[10px] font-bold uppercase tracking-[0.18em] text-[#5C5652] flex-shrink-0">
        {label}
      </div>
      <div className="text-[#2A2624]">{value}</div>
    </div>
  );
}
